import { SAIL_PARTS, type SailPart } from "@nav-games/sailing";

/// Labelled diagram of a Bermudan mainsail. Used both as a reference
/// figure (every part labelled) and as the quiz figure (one part
/// highlighted, the rest unlabelled) in the parts-of-a-sail game.

interface SailPartsFigureProps {
  /// When set, only the named part is labelled and highlighted —
  /// used by the recognition game. When omitted, all parts are
  /// labelled — used in the Reference panel.
  highlight?: SailPart["id"] | null;
  /// When the diagram is in "click a part" mode (game variant
  /// pointing at the figure rather than the choices), pass a click
  /// handler.
  onClickPart?: (id: SailPart["id"]) => void;
  /// Show every part's label even if `highlight` is set — used by
  /// the reveal panel after the student answers.
  showAllLabels?: boolean;
  size?: number;
}

const W = 220;
const H = 240;

const STROKE = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";
const SAIL_FILL = "#eef6ff";
const SAIL_STROKE = "#9aa8c0";
const ACCENT = "#c0382b";

/// Approximate corners of the mainsail triangle inside the viewbox.
/// Head at top-left, tack at bottom-left (gooseneck), clew at
/// bottom-right (end of boom). The data positions in SAIL_PARTS are
/// in the same 100×100 coordinate space scaled to fit here.
const SAIL_HEAD = { x: 48, y: 30 };
const SAIL_TACK = { x: 48, y: 200 };
const SAIL_CLEW = { x: 195, y: 200 };
const MAST_TOP = { x: 40, y: 16 };
const MAST_BASE = { x: 40, y: 220 };
const BOOM_TIP = { x: 200, y: 210 };

/// Where each labelled callout sits relative to the part's position.
/// Pushes the label box out into clear space so it doesn't sit on
/// the sail. Per-part overrides only when the default would overlap.
const LABEL_OFFSET: Partial<Record<SailPart["id"], { dx: number; dy: number }>> = {
  head: { dx: 0, dy: -10 },
  tack: { dx: -8, dy: 14 },
  clew: { dx: 14, dy: 14 },
  luff: { dx: -50, dy: 0 },
  leech: { dx: 40, dy: -4 },
  foot: { dx: 0, dy: 28 },
  batten: { dx: 40, dy: -22 },
  telltale: { dx: -54, dy: -8 },
  "reef-point": { dx: 40, dy: 6 },
  cringle: { dx: -56, dy: 12 },
  boom: { dx: 0, dy: 34 },
  mast: { dx: -42, dy: 0 },
  kicker: { dx: -10, dy: 28 },
  outhaul: { dx: 20, dy: 28 },
  halyard: { dx: -52, dy: 0 },
  sheet: { dx: 26, dy: 18 },
};

function scaleX(x: number): number {
  // SAIL_PARTS x: 0..100 (mast ≈ 18, clew ≈ 86) → viewbox W
  return 30 + (x / 100) * (BOOM_TIP.x - 30);
}

function scaleY(y: number): number {
  // SAIL_PARTS y: 0..100 (head ≈ 14, tack ≈ 90) → viewbox H
  return 16 + (y / 100) * (MAST_BASE.y - 16);
}

export function SailPartsFigure({
  highlight,
  onClickPart,
  showAllLabels = false,
  size = W,
}: SailPartsFigureProps) {
  const interactive = !!onClickPart;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width={size}
      height={(size * H) / W}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={
        highlight
          ? `Sail diagram — highlight the ${highlight}`
          : "Labelled diagram of a Bermudan mainsail"
      }
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      {/* Mast */}
      <line
        x1={MAST_TOP.x}
        y1={MAST_TOP.y}
        x2={MAST_BASE.x}
        y2={MAST_BASE.y}
        stroke={STROKE}
        strokeWidth={4}
      />
      {/* Boom */}
      <line
        x1={MAST_BASE.x}
        y1={SAIL_TACK.y}
        x2={BOOM_TIP.x}
        y2={BOOM_TIP.y}
        stroke={STROKE}
        strokeWidth={3.5}
      />
      {/* Kicker — diagonal from boom (close to gooseneck) down to mast base. */}
      <line
        x1={scaleX(26)}
        y1={scaleY(95) - 4}
        x2={MAST_BASE.x + 2}
        y2={MAST_BASE.y - 6}
        stroke={MUTED}
        strokeWidth={1.5}
      />
      {/* Halyard — runs up the mast then down to the head externally. */}
      <line
        x1={MAST_TOP.x - 2}
        y1={MAST_TOP.y}
        x2={MAST_TOP.x - 2}
        y2={SAIL_HEAD.y - 2}
        stroke={MUTED}
        strokeWidth={1.2}
        strokeDasharray="3 3"
      />
      <line
        x1={MAST_TOP.x - 2}
        y1={SAIL_HEAD.y - 2}
        x2={SAIL_HEAD.x}
        y2={SAIL_HEAD.y}
        stroke={MUTED}
        strokeWidth={1.2}
        strokeDasharray="3 3"
      />
      {/* Sheet — from clew down past boom-end. */}
      <line
        x1={SAIL_CLEW.x + 2}
        y1={SAIL_CLEW.y}
        x2={SAIL_CLEW.x + 6}
        y2={SAIL_CLEW.y + 26}
        stroke={MUTED}
        strokeWidth={1.2}
        strokeDasharray="3 3"
      />

      {/* Sail itself — triangle with a slight roach on the leech. */}
      <path
        d={`
          M ${SAIL_HEAD.x} ${SAIL_HEAD.y}
          L ${SAIL_TACK.x} ${SAIL_TACK.y}
          L ${SAIL_CLEW.x} ${SAIL_CLEW.y}
          Q ${SAIL_CLEW.x + 8} ${(SAIL_HEAD.y + SAIL_CLEW.y) / 2} ${SAIL_HEAD.x} ${SAIL_HEAD.y}
          Z
        `}
        fill={SAIL_FILL}
        stroke={SAIL_STROKE}
        strokeWidth={1.5}
      />

      {/* Battens — three short lines parallel to the boom. */}
      {[0.32, 0.55, 0.78].map((t, i) => {
        const x1 = SAIL_HEAD.x + 3;
        const y1 = SAIL_HEAD.y + t * (SAIL_TACK.y - SAIL_HEAD.y);
        const x2 =
          SAIL_HEAD.x + (1 - t) * 8 + t * (SAIL_CLEW.x - SAIL_HEAD.x) * 0.95;
        const y2 = y1;
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={SAIL_STROKE}
            strokeWidth={1}
          />
        );
      })}

      {/* Reef points — dotted row low on the sail. */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const x =
          SAIL_HEAD.x + 12 + i * 22;
        const y = scaleY(78);
        return (
          <circle key={i} cx={x} cy={y} r={1.2} fill={SAIL_STROKE} />
        );
      })}

      {/* Telltale ribbon — small mark on the luff. */}
      <line
        x1={scaleX(30) - 2}
        y1={scaleY(38)}
        x2={scaleX(30) + 8}
        y2={scaleY(38) - 6}
        stroke="#c08428"
        strokeWidth={1.5}
      />

      {/* Highlighted part dot, if any. */}
      {SAIL_PARTS.map((p) => {
        const cx = scaleX(p.x);
        const cy = scaleY(p.y);
        const isFocus = highlight === p.id;
        const shouldShow = showAllLabels || !highlight || isFocus;
        return (
          <g
            key={p.id}
            style={{ cursor: interactive ? "pointer" : "default" }}
            onClick={() => onClickPart?.(p.id)}
          >
            {/* Hit-target halo for the click variant — bigger than
                the visible dot so finger-tap is forgiving. */}
            {interactive && (
              <circle
                cx={cx}
                cy={cy}
                r={14}
                fill="transparent"
                pointerEvents="all"
              />
            )}
            <circle
              cx={cx}
              cy={cy}
              r={isFocus ? 5 : 3}
              fill={isFocus ? ACCENT : STROKE}
              stroke={isFocus ? "white" : "none"}
              strokeWidth={isFocus ? 1.5 : 0}
            />
            {shouldShow && !interactive && (
              <Callout
                cx={cx}
                cy={cy}
                offset={LABEL_OFFSET[p.id] ?? { dx: 12, dy: 0 }}
                text={p.name}
                emphasis={isFocus}
              />
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Callout({
  cx,
  cy,
  offset,
  text,
  emphasis,
}: {
  cx: number;
  cy: number;
  offset: { dx: number; dy: number };
  text: string;
  emphasis: boolean;
}) {
  const tx = cx + offset.dx;
  const ty = cy + offset.dy;
  const anchor: "start" | "middle" | "end" =
    offset.dx > 6 ? "start" : offset.dx < -6 ? "end" : "middle";
  return (
    <>
      <line
        x1={cx}
        y1={cy}
        x2={tx}
        y2={ty}
        stroke={emphasis ? ACCENT : MUTED}
        strokeWidth={0.7}
      />
      <text
        x={tx + (anchor === "start" ? 2 : anchor === "end" ? -2 : 0)}
        y={ty + 3}
        fontSize={9}
        fill={emphasis ? ACCENT : "#1f2937"}
        fontWeight={emphasis ? 700 : 500}
        textAnchor={anchor}
      >
        {text}
      </text>
    </>
  );
}
